import React, { useEffect, useState } from 'react';
import { X, Info, AlertTriangle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../redux/store';
import { dismissToast, markAsRead, ToastNotification } from '../../../redux/slices/toastSlice';
import './Notifications.css';

// ── Helpers ────────────────────────────────────────────────────────────────────

function getIcon(priority: string) {
  switch (priority) {
    case 'urgent':  return <AlertTriangle size={20} className="nt-icon nt-icon--urgent nt-icon--pulse" />;
    case 'warning': return <AlertTriangle size={20} className="nt-icon nt-icon--warning" />;
    default:        return <Info size={20} className="nt-icon nt-icon--info" />;
  }
}

function getPriorityClass(priority: string) {
  switch (priority) {
    case 'urgent':  return 'nt-toast--urgent';
    case 'warning': return 'nt-toast--warning';
    default:        return 'nt-toast--info';
  }
}

function getProgressClass(priority: string) {
  switch (priority) {
    case 'urgent':  return 'nt-progress--urgent';
    case 'warning': return 'nt-progress--warning';
    default:        return 'nt-progress--info';
  }
}

// ── Toast individual ───────────────────────────────────────────────────────────

interface ToastItemProps {
  notification: ToastNotification;
  onDismiss: (id: number) => void;
  onMarkAsRead: (id: number) => void;
}

function ToastItem({ notification, onDismiss, onMarkAsRead }: ToastItemProps) {
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    const duration = 6000;
    const intervalTime = 50;
    const decrement = (intervalTime / duration) * 100;

    const timer = setInterval(() => {
      setProgress(prev => {
        if (prev <= 0) {
          clearInterval(timer);
          onDismiss(notification.id);
          return 0;
        }
        return prev - decrement;
      });
    }, intervalTime);

    return () => clearInterval(timer);
  }, [notification.id, onDismiss]);

  const handleDismiss = () => {
    onMarkAsRead(notification.id);
    onDismiss(notification.id);
  };

  return (
    <motion.div
      layout
      className={`nt-toast ${getPriorityClass(notification.priority)}`}
      initial={{ opacity: 0, x: -100, scale: 0.9 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: -100, scale: 0.95 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
    >
          {/* ── Cuerpo del toast ── */}
      <div className="nt-toast__body">

        {/* Ícono */}
        <div className="nt-toast__icon-wrap">
          {getIcon(notification.priority)}
        </div>

        {/* Contenido */}
        <div className="nt-toast__content">
          <div className="nt-toast__top">
            <span className="nt-toast__label">Nueva Notificación</span>
            <button className="nt-toast__close" onClick={handleDismiss}>
              <X size={14} />
            </button>
          </div>
          <h4 className="nt-toast__title">{notification.title}</h4>
          <p className="nt-toast__message">{notification.message}</p>
        </div>
      </div>

      {/* ── Barra de progreso ── */}
      <div className="nt-progress-track">
        <div
          className={`nt-progress-bar ${getProgressClass(notification.priority)}`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </motion.div>
  );
}

// ── Contenedor de la pila ──────────────────────────────────────────────────────

export default function NotificationToast() {
  const toastStack = useSelector((state: RootState) => state.toast.toastStack);
  const dispatch = useDispatch();

  const handleDismissToast = (id: number) => {
    dispatch(dismissToast(id));
  };

  const handleMarkAsRead = (id: number) => {
    dispatch(markAsRead(id));
  };

  return (
    <div className="nt-toast-stack">
      <AnimatePresence initial={false}>
        {toastStack.map(toast => (
          <ToastItem
            key={toast.id}
            notification={toast}
            onDismiss={handleDismissToast}
            onMarkAsRead={handleMarkAsRead}
          />
        ))}
      </AnimatePresence>
    </div>
  );
}
